import { classes } from 'html-classes';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { CartNotification } from '~/components/Header/components/CartNotification';
import useNavigateCart from '~/hooks/useNavigateCart';

import { cartStore } from '~/stores';

import Icon from '../../../components/Icon/Icon';

import styles from './MobileHeaderLayout.module.scss';
import MobileHeaderLayoutLocalee from './MobileHeaderLayoutLocalee';

interface MobileHeaderLayoutProps {
  showCart?: boolean;
  content?: React.ReactElement;
  showSearch?: boolean;
  showTopCart?: boolean;
  className?: string;
  showPersonalData?: boolean;
  isSticky?: boolean;
  toolbarContentClassName?: string;
  toolbarClassName?: string;
}

const SEARCH_PAGE_LIST = ['/shop', '/explore', '/c/'];
const MAIN = '/';

const MobileHeaderLayout = ({
  showCart,
  content,
  showSearch,
  showTopCart,
  className,
  showPersonalData,
  isSticky,
  toolbarContentClassName,
  toolbarClassName,
}: MobileHeaderLayoutProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleNavigateCart = useNavigateCart({
    withReplace: true,
    source: 'MobileHeaderLayout',
  });

  const handleNavigatePersonalData = () => {
    navigate('/cabinet/personal-data');
  };

  if (DEFAULT_COMPANIES.includes(company.name)) {
    return (
      <MobileHeaderLayoutLocalee
        content={content}
        showSearch={showSearch}
        className={className}
        showPersonalData={showPersonalData}
        isSticky={isSticky}
        toolbarContentClassName={toolbarContentClassName}
        toolbarClassName={toolbarClassName}
      />
    );
  }

  if (company.newDesignSystemEnabled) {
    return null;
  }

  return (
    <div
      className={classes([
        styles.container,
        isSticky && styles.sticky,
        className,
      ])}
      data-company={company.name}
    >
      {(content || showTopCart) && (
        <div
          className={classes([
            styles.toolbar,
            'content-layout',
            toolbarClassName,
          ])}
        >
          <div
            className={classes([
              styles.toolbarContent,
              toolbarContentClassName,
            ])}
          >
            {content}
          </div>
          {showTopCart && (
            <div
              data-button="header-mobile-cart"
              className={classes([
                styles.cart,
                cartStore.totalCartCount && styles.cartFull,
              ])}
              onClick={handleNavigateCart}
            >
              <Icon type="bag" size={24} />
              <div className={styles.totalCartCount}>
                {cartStore.totalCartCount}
              </div>
            </div>
          )}
          {showPersonalData && (
            <div
              data-button="header-mobile-personal-data"
              className={classes([
                styles.cart,
                cartStore.totalCartCount && styles.cartFull,
              ])}
              onClick={handleNavigatePersonalData}
            >
              <Icon type="filter" size={24} />
              <div className={styles.totalCartCount}>
                {cartStore.totalCartCount}
              </div>
            </div>
          )}
        </div>
      )}
      {(pathname === MAIN ||
        SEARCH_PAGE_LIST.some((i) => pathname.includes(i)) ||
        showSearch) && (
        <div
          className={classes([
            'content-layout header-search-container',
            styles.searchBar,
          ])}
        >
          <Link
            data-link="search"
            className={classes([
              'header-search',
              showCart &&
                company.isAvailableCartInMobileHeader &&
                styles.searchInput,
            ])}
            to="/search"
            replace={true}
            data-company={company.name}
          >
            <Icon className="header-search__icon" type="search" size={24} />

            <div className="header-search__text">{t('phrases:searchFor')}</div>
          </Link>
          {showCart && content && company.isAvailableCartInMobileHeader && (
            <div className={styles.toolbar}>
              <div
                data-button="header-go-to-cart"
                className={classes([
                  styles.cart,
                  cartStore.totalCartCount && styles.cartFull,
                ])}
                onClick={handleNavigateCart}
              >
                <Icon type="bag" size={24} />
                <div className={styles.totalCartCount}>
                  {cartStore.totalCartCount}
                </div>
                <CartNotification />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default observer(MobileHeaderLayout);
